import './i18n';
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { LoginPage, RegisterPage, PendingApprovalPage, DashboardPage, AddOrEditListingPage, ClaimPage } from './pages';
import { Navbar, Notification } from './components';
import LottieSpinner from './components/LottieSpinner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';
import Footer from './components/Footer';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, agentData, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><LottieSpinner size={100} /></div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!agentData?.isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { globalNotification, currentUser, agentData } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t('site_title', 'HomeMe Web Portal');
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen flex flex-col">
      {currentUser && agentData?.isApproved && <Navbar />}
      <main className={`flex-grow ${currentUser && agentData?.isApproved ? 'pt-16' : ''}`}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pending-approval" element={<PendingApprovalPage />} />
          {/* Publiek: claim-link uit HomeMe-mails; werkt ook vóór approval (CF keurt automatisch goed) */}
          <Route path="/claim/:listingId" element={<ClaimPage />} />
          
          <Route 
            path="/dashboard" 
            element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} 
          />
          <Route 
            path="/add-listing" 
            element={<ProtectedRoute><AddOrEditListingPage /></ProtectedRoute>} 
          />
          <Route 
            path="/edit-listing/:listingId" 
            element={<ProtectedRoute><AddOrEditListingPage /></ProtectedRoute>} 
          />
          <Route path="/" element={<NavigateToCorrectPage />}/>
          <Route path="*" element={<div className="text-center p-10"><h2>404 - Pagina niet gevonden</h2><Link to="/dashboard" className="text-homeme hover:underline">Terug naar Dashboard</Link></div>} />
        </Routes>
      </main>
      {globalNotification && (
        <Notification
          message={globalNotification.message}
          type={globalNotification.type === "info" ? "success" : globalNotification.type}
          onClose={() => { /* Notification auto-closes or context handles it */ }}
        />
      )}
      {currentUser && agentData?.isApproved && (
        <Footer />
      )}
    </div>
  );
};

const NavigateToCorrectPage: React.FC = () => {
  const { currentUser, agentData, loading } = useAuth();

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><LottieSpinner size={100} /></div>;
  }

  if (currentUser) {
    if (agentData?.isApproved) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/pending-approval" replace />;
  }
  return <Navigate to="/login" replace />;
};

export default App;
